import { useState, useCallback, useMemo } from 'react';

const PAGE_SIZE_OPTIONS = [50, 100, 200, 300] as const;
const STORAGE_PREFIX = 'cbt_pageSize_';
const DEFAULT_PAGE_SIZE = 50;

function getStoredPageSize(key: string): number {
  try {
    const stored = localStorage.getItem(STORAGE_PREFIX + key);
    if (stored) {
      const num = parseInt(stored, 10);
      if ((PAGE_SIZE_OPTIONS as readonly number[]).includes(num)) return num;
    }
  } catch { /* SSR / private browsing */ }
  return DEFAULT_PAGE_SIZE;
}

export function usePagination(storageKey: string) {
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPageState] = useState(() => getStoredPageSize(storageKey));

  const setPerPage = useCallback((size: number) => {
    setPerPageState(size);
    setPage(1);
    try { localStorage.setItem(STORAGE_PREFIX + storageKey, String(size)); } catch { /* noop */ }
  }, [storageKey]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / perPage)), [total, perPage]);

  const safeSetPage = useCallback((p: number) => {
    setPage(Math.max(1, Math.min(p, totalPages)));
  }, [totalPages]);

  // Reset to page 1 helper (for filter changes)
  const resetPage = useCallback(() => setPage(1), []);

  return {
    page,
    setPage: safeSetPage,
    perPage,
    setPerPage,
    total,
    setTotal,
    totalPages,
    resetPage,
    PAGE_SIZE_OPTIONS,
  };
}
